<template>
  <div>
    <h4 class="member-details-form-section-header">Contact information</h4>
    <caption class="required-field-indicator">* Indicates required field</caption>
    <Alert v-if="showAlert" :message="refData.contactInformationAlertText.informationText" alertType="info" />

    <div class="form-section">
      <ContactInfo
        :getRequired="getRequired('preferredPhoneNo')"
        :initialState="memberDetails && memberDetails.contactInformation.preferredContactMethods.length !== 0 ? memberDetails.contactInformation.preferredContactMethods : null"
        :phoneNumberTypeList="phoneNumberTypeList"
        :labelText="'Preferred phone number'"
        :id="'contactInformation'"
        :readOnlyTypes="mobileReadOnlyTypes"
        :helperText="mobileAccountSecurityHelpText"
        @updateSuppressCommunicationFlag="updateSuppressCommunicationFlag"
        @updatePreferredContactMethods="updatePreferredContactMethods"
        :componentName="'ContactInformation'"
      />
    </div>

    <div class="form-section">
      <FormField
        id="email"
        type="email"
        autocomplete="email"
        v-model.lazy="v$.memberDetails.contactInformation.email.$model"
        labelText="Email address"
        :required="getRequired('email')"
        :disabled="isEmailLocked"
        :helperText="emailAccountSecurityHelpText"
        :error="v$.memberDetails.contactInformation.email.$invalid"
        :errorMessage="getEmailErrorMessage(v$.memberDetails.contactInformation.email)"
        placeholder="info@defencehealth.com.au"
      />
    </div>

    <div class="form-section">
      <FormField id="postal" labelText="Postal address" :required="getRequired('postalAddress')" :helperText="refData.postalAddressHelpText.informationText">
        <fieldset>
          <div class="postal-address-checkbox-wrapper">
            <input type="checkbox" id="POBoxCheck" v-model="isPOBox" />
            <label for="POBoxCheck">My postal address is a Post Office box (PO Box)</label>
          </div>
        </fieldset>

        <AddressForm @updateAddressDetail="updateAddressDetail" :isPOBox="isPOBox" :preFilledDetails="postalAddress" />
        <Alert v-if="postalAddressUpdated && (multiplePolicyHolderMemberships || multipleMembershipsAndAtLeastOnePolicyHolder)" alertType="info" class="inline-message">
          <div>
            <p>Updates to postal address will reflect across all policies this member is on.</p>
          </div>
        </Alert>
      </FormField>
    </div>

    <div class="form-section">
      <FormField
        id="prefcommmethod"
        labelText="Preferred communication method"
        :helperText="refData.preferredCommunicationHelpText.informationText"
        :required="getRequired('communicationMethod')"
      >
        <ul class="radio-group large">
          <li v-for="option in refData.preferredCommunicationOptions" :key="option.returnValue">
            <!-- FIXME: value to upper case is temp fix from FE. when create membership api updated to accept 
            preferred comm method value to be all upper case, remove `.toUpperCase()` here -->
            <input
              v-model.lazy="v$.memberDetails.contactInformation.preferredCommunicationMethod.$model"
              type="radio"
              :id="`preferred-communication-${option.display}`"
              :value="option.returnValue.toUpperCase()"
              name="communicationMethod"
            />
            <label :for="`preferred-communication-${option.display}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>

    <div v-if="getFieldOptions().paymentAdvice.hidden !== true" class="form-section">
      <FormField id="claimPayment" labelText="When we pay a claim, would you like to receive a payment advice?" :helperText="refData.paymentAdviceHelpText.informationText">
        <ul class="radio-group large">
          <li v-for="option in refData.paymentAdviceOptions" :key="option.returnValue">
            <input
              v-model.lazy="v$.memberDetails.contactInformation.paymentAdvice.$model"
              type="radio"
              :id="`payment-advice-${option.display}`"
              :value="option.returnValue"
              name="paymentAdvice"
            />
            <label :for="`payment-advice-${option.display}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import AddressForm from '@general/js/components/AddressForm.vue';
import ContactInfo from '@general/js/components/ContactInfo.vue';
import { getEmailErrorMessage } from '@/utils/ErrorMessages';
import { Alert } from '@/components';
import { firstLetterToUpperCase, isPOBoxAddress } from '@general/js/utils';
import { ContactNumberType, UpdateMemberDetailsStepNames } from '@/enums';
import MemberDetailsMixin from './MemberDetailsMixin';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: UpdateMemberDetailsStepNames.step2,

  mixins: [MemberDetailsMixin],

  components: {
    ContactInfo,
    Alert,
    AddressForm,
  },

  props: {
    initialData: {
      type: Object,
    },
  },

  data() {
    return {
      postalAddress: {
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        postcode: '',
        state: '',
        suburb: '',
      },
      fieldOptions: {
        PolicyHolder: {
          preferredPhoneNo: {
            required: true,
          },
          postalAddress: {
            required: true,
          },
          email: {
            required: true,
          },
          communicationMethod: {
            required: true,
          },
          paymentAdvice: {
            required: false,
            hidden: true,
          },
        },
        Dependant: {
          preferredPhoneNo: {
            required: false,
          },
          postalAddress: {
            required: true,
          },
          email: {
            required: false,
          },
          communicationMethod: {
            required: false,
          },
          paymentAdvice: {
            required: false,
            hidden: true,
          },
        },
      },
      addressFormInvalid: false,
      isPOBox: false,
      /**
       * list of available options that reflect what we have in BE
       * it should be always the same as what we have in BE
       */
      phoneNumberTypeList: [
        {
          display: firstLetterToUpperCase(ContactNumberType.MOBILE),
          returnValue: firstLetterToUpperCase(ContactNumberType.MOBILE),
        },
        {
          display: firstLetterToUpperCase(ContactNumberType.WORK),
          returnValue: firstLetterToUpperCase(ContactNumberType.WORK),
        },
        {
          display: firstLetterToUpperCase(ContactNumberType.HOME),
          returnValue: firstLetterToUpperCase(ContactNumberType.HOME),
        },
      ],
      isPreferredContactMethodsInvalid: false,
    };
  },

  validations() {
    return this.validationObj;
  },

  beforeMount() {
    this.postalAddress = this.initialData?.contactInformation?.postalAddress;
    this.isPOBox = isPOBoxAddress(this.postalAddress.addressLine1);
    // Payment advice field is hidden; always default to 'Yes' so the backend receives the correct value
    this.updateMemberDetails({
      ...this.memberDetails,
      contactInformation: {
        ...this.memberDetails?.contactInformation,
        paymentAdvice: true,
      },
    });
  },

  methods: {
    getEmailErrorMessage,

    updateAddressDetail(addressDetail) {
      this.postalAddress = { ...this.postalAddress, ...addressDetail.address };
      this.addressFormInvalid = addressDetail.invalid;
    },

    getRequired(field) {
      const commMethod = this.memberDetails?.contactInformation?.preferredCommunicationMethod;

      // For a dependant email fields are only required if they are selected as a communication method
      if (this.isDependant && field.toLowerCase() === 'email' && commMethod === 1) {
        return true;
      }

      // Address fields
      const addressFields = ['addressLine1', 'addressLine2', 'addressLine3', 'suburb', 'postcode', 'state'];
      if (addressFields.includes(field.toLowerCase())) {
        return true;
      }

      // Default options
      return this.getFieldOptions()[field].required;
    },

    getFieldOptions() {
      if (this.isDependant) {
        return this.fieldOptions.Dependant;
      }
      return this.fieldOptions.PolicyHolder;
    },

    updateSuppressCommunicationFlag(flag) {
      if (this.memberDetails) {
      this.v$.memberDetails.contactInformation.suppressCommunicationFlag.$model = flag;
      }
    },

    updatePreferredContactMethods(data) {
      this.updateMemberDetails({
        ...this.memberDetails,
        contactInformation: {
          ...this.memberDetails?.contactInformation,
          preferredContactMethods: data.phoneNumbersArr,
        },
      });
      this.isPreferredContactMethodsInvalid = data.isInvalid;
    },
  },

  watch: {
    postalAddress: {
      handler(newObj, oldObj) {
        if(this.memberDetails){
          const newMemberDetails = { ...this.memberDetails };
          newMemberDetails.contactInformation.postalAddress = { ...oldObj, ...newObj };
          this.updateMemberDetails(newMemberDetails);
        }
      },
      deep: true,
    },
  },

  computed: {
    isEmailLocked() {
      return !!this.memberDetails?.registrationStatus;
    },
    isMobileLocked() {
      return !!this.memberDetails?.mfaRegistrationStatus;
    },
    mobileReadOnlyTypes() {
      return this.isMobileLocked ? [firstLetterToUpperCase(ContactNumberType.MOBILE)] : [];
    },
    // Content-managed account security messaging, worded differently when viewing own vs another member's locked details.
    mobileAccountSecurityHelpText() {
      if (!this.isMobileLocked) return '';
      return this.loggedInUserAccessingThemselves
        ? this.refData.mobileAccountSecurityHelpText.informationText
        : this.refData.mobileAccountSecurityOtherMemberHelpText.informationText;
    },
    emailAccountSecurityHelpText() {
      if (!this.isEmailLocked) return '';
      return this.loggedInUserAccessingThemselves
        ? this.refData.emailAccountSecurityHelpText.informationText
        : this.refData.emailAccountSecurityOtherMemberHelpText.informationText;
    },
    validationObj() {
      const obj = {
        addressFormInvalid: {
          isValid(bool) {
            return bool === false;
          },
        },
        isPreferredContactMethodsInvalid: {
          isValid(bool) {
            return bool === false;
          },
        },
        memberDetails: {
          contactInformation: {
            paymentAdvice: {},
            email: { email },
            preferredCommunicationMethod: {},
            suppressCommunicationFlag: {},
          },
        },
      };
      const contactInfoObj = obj.memberDetails.contactInformation;

      if (this.getRequired('paymentAdvice')) {
        contactInfoObj.paymentAdvice = { ...contactInfoObj.paymentAdvice, required };
      }

      if (this.getRequired('email')) {
        contactInfoObj.email = { ...contactInfoObj.email, required };
      }

      if (this.getRequired('communicationMethod')) {
        contactInfoObj.preferredCommunicationMethod = {
          ...contactInfoObj.preferredCommunicationMethod,
          required,
          isValidValue(value) {
            const validValues = this.refData.preferredCommunicationOptions.map((option) => {
              return option.returnValue;
            });

            return validValues.some((returnValue) => {
              return returnValue.localeCompare(value, 'en', { sensitivity: 'accent' }) === 0;
            });
          },
        };
      }

      return obj;
    },

    showAlert() {
      const contactInformation = this.memberDetails?.contactInformation;
      const initialData = this.initialData?.contactInformation ?? {};
      const isSuppressCommunicationFlagOn = contactInformation?.suppressCommunicationFlag;
      const methodChanged = contactInformation?.preferredCommunicationMethod !== initialData?.preferredCommunicationMethod;
      let communicationDetailChanged;
      switch (contactInformation?.preferredCommunicationMethod) {
        case 'EMAIL':
          communicationDetailChanged = !isEqual(contactInformation.email, initialData?.email);
          break;

        case 'POST':
          communicationDetailChanged = !isEqual(contactInformation.postalAddress, initialData?.postalAddress);
          break;

        default:
          break;
      }
      return isSuppressCommunicationFlagOn && !(communicationDetailChanged || methodChanged);
    },

    preferredContactMethods() {
      return this.memberDetails?.contactInformation.preferredContactMethods;
    },
    postalAddressUpdated() {
      const initialPostalAddress = this.initialData?.contactInformation.postalAddress ?? {};
      // Check if any field in the postal address has changed
      return !isEqual(initialPostalAddress, this.memberDetails?.contactInformation.postalAddress);
    },
  },
};
</script>

<style lang="scss">
.postal-address-checkbox-wrapper {
  margin-bottom: spacer(2);
}

.no-bottom-margin {
  margin-bottom: 0 !important;
}

.half-size {
  max-width: calc(50% - #{spacer(0.75)});

  @include mobile {
    max-width: 100%;
  }
}
</style>
